import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import SharedButton from '../../components/Shared/SharedButton';
import SharedInputText from '../../components/Shared/SharedInputText';
import SharedDropdown from '../../components/Shared/SharedDropdown';
import { getERPClient } from '../../services/supplyChainApi';

export default function CreateERPSupplierModal({ visible, onHide, onRefresh, initialData }) {
  const { t } = useTranslation();

  const isEdit = !!initialData?.id;

  const defaultValues = {
    supplier_code: initialData?.supplier_code || initialData?.supplier_global_id || '',
    name_en: initialData?.name_en || initialData?.legal_name_en || '',
    name_ar: initialData?.name_ar || initialData?.legal_name_ar || '',
    category_code: initialData?.category_code || 'CONSUMABLES',
    corporate_status: initialData?.corporate_status || 'LLC',
    vat_no: initialData?.vat_no || initialData?.tax_number || '',
    cr_no: initialData?.cr_no || initialData?.cr_number || '',
    risk_classification_code: initialData?.risk_classification_code || 'LOW',
    system_status: initialData?.system_status || 'ACTIVE',
    business_status: initialData?.business_status || 'PENDING',
    change_reason: '',
  };

  const validationSchema = Yup.object({
    supplier_code: Yup.string().nullable(),
    name_en: Yup.string().required(t('Legal Name (English) is required')),
    name_ar: Yup.string().required(t('Legal Name (Arabic) is required')),
    change_reason: isEdit
      ? Yup.string().required(t('Change Reason is required'))
      : Yup.string().nullable(),
  });

  const categoryOptions = [
    { label: t("Pharma"), value: "PHARMA" },
    { label: t("Medical Devices"), value: "MED_DEVICES" },
    { label: t("Consumables"), value: "CONSUMABLES" },
  ];

  const corporateStatusOptions = [
    { label: t("LLC"), value: "LLC" },
    { label: t("JOINT_STOCK"), value: "JOINT_STOCK" },
    { label: t("SOLE_PROP"), value: "SOLE_PROP" },
    { label: t("PARTNERSHIP"), value: "PARTNERSHIP" },
  ];

  const riskOptions = [
    { label: t("HIGH"), value: "HIGH" },
    { label: t("MEDIUM"), value: "MEDIUM" },
    { label: "CRITICAL", value: "CRITICAL" },
    { label: t("LOW"), value: "LOW" },
  ];

  const systemStatusOptions = [
    { label: t("Active"), value: "ACTIVE" },
    { label: t("Inactive"), value: "INACTIVE" },
  ];

  const businessStatusOptions = [
    { label: t("Pending"), value: "PENDING" },
    { label: t("Approved"), value: "APPROVED" },
    { label: t("Suspended"), value: "SUSPENDED" },
    { label: t("Blacklisted"), value: "BLACKLISTED" },
  ];

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const erpClient = getERPClient();
      const payload = {
        ...values,
        legal_name_en: values.name_en,
        legal_name_ar: values.name_ar,
        tax_number: values.vat_no,
        cr_number: values.cr_no,
        change_reason: values.change_reason,
      };

      if (isEdit) {
        await erpClient.updateSupplier(initialData.id, payload);
        toast.success(t('Supplier updated successfully'));
      } else {
        await erpClient.createSupplier(payload);
        toast.success(t('Supplier created successfully'));
      }
      resetForm();
      onHide();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to save ERP supplier:', error);
      const msg = error.response?.data?.message || t('Error saving supplier');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      header={isEdit ? t('Edit Supplier') : t('Create New Supplier')}
      visible={visible}
      onHide={onHide}
      pt={{
        root: { className: 'w-full lg:w-9/12 mx-4 overflow-auto' },
        header: { className: '!p-3 !bg-darkBg !text-white' },
        content: { className: '!p-4' },
        closeButton: { className: 'hover:!bg-black/20 !outline-none !shadow-none' },
        closeButtonIcon: { className: '!text-white' },
      }}
    >
      <Formik
        initialValues={defaultValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form className="space-y-4">
            <div className="space-y-4">

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
                {!isEdit && (
                  <SharedInputText
                    label={`${t('Supplier Global ID')} `}
                    name="supplier_code"
                    placeholder={t('Enter Supplier Global ID')}
                    error={touched.supplier_code && errors.supplier_code}
                  />
                )}

                <SharedInputText
                  label={`${t('Legal Name (English)')} *`}
                  name="name_en"
                  placeholder={t('Enter Legal Name (English)')}
                  error={touched.name_en && errors.name_en}
                />

                <SharedInputText
                  label={`${t('Legal Name (Arabic)')} *`}
                  name="name_ar"
                  placeholder={t('Enter Legal Name (Arabic)')}
                  error={touched.name_ar && errors.name_ar}
                />

                <SharedDropdown
                  label={t('Category Code')}
                  name="category_code"
                  placeholder={t('Select Category')}
                  options={categoryOptions}
                />

                <SharedDropdown
                  label={t('Corporate Status')}
                  name="corporate_status"
                  placeholder={t('Select Corporate Status')}
                  options={corporateStatusOptions}
                />

                <SharedInputText
                  label={t('Tax Number')}
                  name="vat_no"
                  placeholder={t('Enter Tax Number')}
                  error={touched.vat_no && errors.vat_no}
                />

                <SharedInputText
                  label={t('CR Number')}
                  name="cr_no"
                  placeholder={t('Enter CR Number')}
                  error={touched.cr_no && errors.cr_no}
                />

                <SharedDropdown
                  label={t('Risk Classification')}
                  name="risk_classification_code"
                  placeholder={t('Select Risk Classification')}
                  options={riskOptions}
                />

                {!isEdit && (
                  <>
                    <SharedDropdown
                      label={t('System Status')}
                      name="system_status"
                      placeholder={t('Select System Status')}
                      options={systemStatusOptions}
                    />

                    <SharedDropdown
                      label={t('Business Status')}
                      name="business_status"
                      placeholder={t('Select Business Status')}
                      options={businessStatusOptions}
                    />
                  </>
                )}
              </div>

              {isEdit && (
                <div className="w-full pt-2">
                  <label className="input-label font-medium text-gray-700 block mb-1">
                    {t('Change Reason')} *
                  </label>
                  <SharedInputText
                    name="change_reason"
                    placeholder={t('Enter change reason...')}
                    error={touched.change_reason && errors.change_reason}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
              <SharedButton
                type="button"
                label={t('Cancel')}
                buttonType="reset"
                onClick={onHide}
              />
              <SharedButton
                type="submit"
                label={isEdit ? t('Update') : t('Create')}
                icon={isEdit ? "pi pi-check" : "pi pi-check"}
                buttonType="primary"
                loading={isSubmitting}
              />
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
